/**
 * Sons da app — sintetizados, com UMA excepção gravada.
 *
 * Um "blip" de entrar numa call são dois osciladores e um envelope: gerá-los na
 * Web Audio custa ~2 KB de código e ZERO bytes de rede, licença ou pedido HTTP.
 * Um ficheiro .mp3 seria maior, precisaria de licença e de um round-trip — para
 * 200 ms de "pluc" não compensa. Cada som é uma pequena partitura de tons.
 *
 * A EXCEPÇÃO é o `arrive`: quando alguém entra numa call toca o som da marca
 * (`/sounds/arrive.m4a`, 26 KB de AAC), porque uma identidade sonora é a única
 * coisa que a síntese não inventa. A partitura do `arrive` continua aqui e não
 * é decoração — é o que toca enquanto o ficheiro não chegou, num browser sem
 * AAC, ou sem rede. O som degrada; nunca desaparece.
 *
 * Regras que fazem isto soar a produto e não a besouro:
 *  - ENVELOPE em toda a nota (ataque de ~8 ms, cauda exponencial): sem ele, ligar
 *    e desligar o oscilador dá um "click" seco — o artefacto que grita "amador".
 *  - AudioContext ÚNICO e preguiçoso. Criá-lo por som esgota o limite do browser
 *    (~6 contextos) e deixa a app muda a meio. Criamos um e destrancamo-lo no
 *    primeiro gesto do utilizador (política de autoplay).
 *  - SOFT-CLIP no master. Sem ele, o tecto do sistema é uma tesoura: passar de 1.0
 *    corta a onda a direito e o que se ouve é estalido, não volume. Com uma tanh
 *    à saída, empurrar mais sinal para dentro dá mais volume e satura suave.
 *  - NUNCA rebenta. Sem Web Audio, sem gesto ainda, aba em segundo plano — o som
 *    é um extra; falhar em silêncio é o comportamento correcto, não um erro.
 */

export type SoundName =
  | "join"
  | "arrive"
  | "leave"
  | "notify"
  | "mute"
  | "unmute"
  | "poke"
  | "toque"
  | "chamando"
  | "atendida"
  | "fimLigacao";

/** Uma nota: frequência (Hz), início e duração (s) relativos ao disparo, forma
 *  de onda e ganho relativo dentro do som.
 *
 *  `sus` escolhe o ENVELOPE, e é a diferença entre um som que se ouve e um que
 *  não. Sem `sus`, a nota é um "pluc": sobe ao pico e cai exponencialmente até
 *  ~silêncio ao fim de `d`. Essa queda tem um número FIXO de e-folds (ln(pico/
 *  0.0001) ≈ 8.5), portanto τ = d/8.5 e a nota já caiu 20 dB aos ~30% de `d` —
 *  seja `d` 140 ms ou 420 ms. Ou seja: num pluc, aumentar `d` NÃO acrescenta
 *  energia nenhuma, só estica a mesma cauda. Com `sus` (0..1) a nota ganha um
 *  PATAMAR a `sus × pico` que dura de facto, e aí `d` passa a valer volume. */
interface Note {
  f: number;
  t: number;
  d: number;
  type?: OscillatorType;
  gain?: number;
  sus?: number;
}

/**
 * As partituras. Ascendente = algo abriu/entrou; descendente = fechou/saiu — a
 * direcção do intervalo carrega o significado sem o utilizador pensar nisso.
 */
const SCORES: Record<SoundName, Note[]> = {
  // ENTREI EU: arpejo ascendente D5 → A5 → D6 em triangle, a ~2.6× o tecto. É a
  // confirmação do meu próprio clique: já sei que carreguei, o som só fecha o
  // gesto, por isso fica no triangle fino e curto (pluc, sem `sus`). Não se mexe
  // nele quando se quer ouvir melhor as chegadas — é para isso que o `arrive`
  // existe em separado. Serve de REFERÊNCIA de volume ao teste: tudo o resto é
  // medido contra este.
  join: [
    { f: 587.33, t: 0, d: 0.14, type: "triangle", gain: 2.6 },
    { f: 880.0, t: 0.11, d: 0.16, type: "triangle", gain: 2.6 },
    { f: 1174.66, t: 0.22, d: 0.24, type: "triangle", gain: 2.4 },
  ],
  // ENTROU ALGUÉM: uma fanfarra de consola — arpejo de Dó maior a subir, em onda
  // QUADRADA, com uma bordadura no topo e o acorde a segurar por baixo.
  //
  // Hoje esta partitura é o SUPLENTE: o titular é `/sounds/arrive.m4a`, o som da
  // marca. Fica cá porque o suplente tem de conseguir entrar em campo sozinho —
  // sem rede, num browser sem AAC, ou nos milissegundos antes de o ficheiro estar
  // descodificado — e nesse minuto tem de soar a produto e não a bip de avaria.
  // Persegue o mesmo efeito: aquele "entraste no jogo" de oito bits, que não está
  // no ficheiro de ninguém — está em três escolhas de síntese:
  //  - QUADRADA no lead, e é a decisão toda. Tem os harmónicos ímpares a cair
  //    1/n (4/πn) contra 1/n² da triangular (8/π²n²): muito mais energia nos
  //    2-4 kHz, que é onde a ponderação A é máxima. É o que faz ler-se como
  //    consola em vez de sino — e, de caminho, é o que a torna alta sem ganho.
  //  - NOTAS ENCAVALITADAS a 75 ms. O arpejo tem de CORRER: a esta cadência é um
  //    power-up, a metade dela era uma harpa. Cada nota dura 400 ms com patamar,
  //    portanto elas soam JUNTAS — o arpejo constrói o acorde em vez de o soletrar.
  //  - A BORDADURA no topo (A6 de 200 ms entre duas G6) é o que separa isto de
  //    uma escala. Sem ela o arpejo acaba; com ela, CHEGA.
  // O acorde grave em serra entra desencontrado 20 ms para os dois ataques não
  // somarem pico, e a cintilação (C7) entra 65 ms depois da G6 longa pela mesma
  // razão — juntas, custavam pico e não davam volume.
  //
  // Volume: +21.78 dB sobre o `join` (A-ponderado), +19.90 à SAÍDA da tanh,
  // exactamente onde estava o som anterior. Não é coincidência: o que importa é
  // a ORDEM entre os sons, e o `poke` tem de continuar acima — se este subisse,
  // uma chegada qualquer passava a soar tão urgente como alguém a chamar por mim.
  //
  // E o último dB não veio de ganho nenhum. A 2.15 de ganho o pico batia em 2.04
  // e o tecto são 2.0 (duas chegadas ao mesmo tempo têm de caber nos 4 do
  // master); o caminho foi BAIXAR o ganho para 2.02 e ir buscar a energia ao
  // patamar (`sus` 0.88) e à duração — entre os ataques, onde não há pico a
  // limitar. Pico final 1.892, e 3.78 a dobrar: nunca corta.
  arrive: [
    { f: 130.81, t: 0, d: 0.88, type: "sawtooth", gain: 1.8, sus: 0.62 },
    { f: 196, t: 0.02, d: 0.86, type: "triangle", gain: 1.9, sus: 0.58 },
    { f: 783.99, t: 0, d: 0.4, type: "square", gain: 2.02, sus: 0.88 },
    { f: 1046.5, t: 0.075, d: 0.4, type: "square", gain: 2.02, sus: 0.88 },
    { f: 1318.51, t: 0.15, d: 0.4, type: "square", gain: 2.02, sus: 0.88 },
    { f: 1567.98, t: 0.225, d: 0.42, type: "square", gain: 2.02, sus: 0.88 },
    { f: 1760, t: 0.315, d: 0.2, type: "square", gain: 1.9189999999999998, sus: 0.88 },
    { f: 1567.98, t: 0.375, d: 0.68, type: "square", gain: 2.2, sus: 0.88 },
    { f: 2093, t: 0.44, d: 0.62, type: "sine", gain: 2.9, sus: 0.72 },
  ],
  // Sair: A5 → D5, o mesmo intervalo ao contrário. "Foste."
  leave: [
    { f: 880.0, t: 0, d: 0.12 },
    { f: 587.33, t: 0.09, d: 0.18 },
  ],
  // Notificação: B5 → E6, curto e claro (triangle corta melhor o ruído de fundo).
  notify: [
    { f: 987.77, t: 0, d: 0.09, type: "triangle" },
    { f: 1318.51, t: 0.075, d: 0.13, type: "triangle" },
  ],
  // Silenciar o micro: desce e fecha.
  mute: [
    { f: 520, t: 0, d: 0.07 },
    { f: 340, t: 0.05, d: 0.12 },
  ],
  // Reactivar o micro: sobe e abre.
  unmute: [
    { f: 340, t: 0, d: 0.07 },
    { f: 520, t: 0.05, d: 0.12 },
  ],
  // Chamar a atenção: o único som da app desenhado para INTERROMPER, e por isso o
  // mais alto de todos — tem de ficar ACIMA do `arrive`, senão uma chegada
  // qualquer soa tão urgente como alguém a chamar por mim de propósito. A regra
  // não é o número, é a ORDEM: quando o `arrive` subiu 5.4 dB, este subiu com
  // ele (+7 dB) para não perder o lugar. Fica a +2.4 dB dele, e sobra-lhe tecto
  // porque as batidas são sequenciais — não há acorde nenhum a somar picos.
  // Três coisas o separam do resto:
  //  - `square`, não sawtooth nem triangle. A onda quadrada tem os harmónicos
  //    ímpares todos e atravessa música, teclado e uma sala com gente a falar; é a
  //    diferença entre um aviso e um aviso que se OUVE.
  //  - REPETIÇÃO em vez de arpejo. Uma nota que bate quatro vezes na MESMA altura
  //    lê-se como campainha; um arpejo lê-se como notificação, e a notificação é
  //    exactamente aquilo que já se aprendeu a ignorar.
  //  - E6/A5 alternados, na banda em que o ouvido é mais sensível, cada batida com
  //    patamar para a pancada ter corpo em vez de ser um tique.
  // Continua a durar meio segundo: o objectivo é fazer virar a cabeça, não pôr
  // ninguém a procurar o botão de desligar o som.
  poke: [
    { f: 1318.51, t: 0, d: 0.13, type: "square", gain: 7.6, sus: 0.8 },
    { f: 880.0, t: 0.12, d: 0.13, type: "square", gain: 7.6, sus: 0.8 },
    { f: 1318.51, t: 0.24, d: 0.13, type: "square", gain: 7.6, sus: 0.8 },
    { f: 880.0, t: 0.36, d: 0.3, type: "square", gain: 7.2, sus: 0.75 },
  ],
  // ── Ligações privadas ──
  // O TOQUE de quem recebe: dois trinados de E6/A6 em onda quadrada, como um
  // telemóvel. Toca em repetição (`tocarEmRepeticao`) até se atender ou recusar.
  // Abaixo do `poke` em ganho: toca muitas vezes, e tem de chamar sem irritar.
  toque: [
    { f: 1318.51, t: 0, d: 0.1, type: "square", gain: 4.4, sus: 0.7 },
    { f: 1760, t: 0.1, d: 0.1, type: "square", gain: 4.4, sus: 0.7 },
    { f: 1318.51, t: 0.2, d: 0.1, type: "square", gain: 4.4, sus: 0.7 },
    { f: 1760, t: 0.3, d: 0.16, type: "square", gain: 4.4, sus: 0.7 },
    { f: 1318.51, t: 0.62, d: 0.1, type: "square", gain: 4.4, sus: 0.7 },
    { f: 1760, t: 0.72, d: 0.1, type: "square", gain: 4.4, sus: 0.7 },
    { f: 1318.51, t: 0.82, d: 0.1, type: "square", gain: 4.4, sus: 0.7 },
    { f: 1760, t: 0.92, d: 0.22, type: "square", gain: 4.4, sus: 0.7 },
  ],
  // O "A CHAMAR" de quem liga: o sinal de chamada do telefone — 440 + 480 Hz
  // juntos durante um segundo, e silêncio até à repetição. Suave: é espera.
  chamando: [
    { f: 440, t: 0, d: 1, type: "sine", gain: 2.2, sus: 0.85 },
    { f: 480, t: 0, d: 1, type: "sine", gain: 2.2, sus: 0.85 },
  ],
  // Atendeu: duas notas a subir, curtas. "Estás ligado."
  atendida: [
    { f: 659.25, t: 0, d: 0.12, type: "triangle", gain: 2.6 },
    { f: 987.77, t: 0.1, d: 0.22, type: "triangle", gain: 2.6 },
  ],
  // Desligou (ou não deu): três "tu" graves, o fim de chamada de sempre.
  fimLigacao: [
    { f: 480, t: 0, d: 0.18, type: "sine", gain: 2.8, sus: 0.8 },
    { f: 480, t: 0.3, d: 0.18, type: "sine", gain: 2.8, sus: 0.8 },
    { f: 480, t: 0.6, d: 0.18, type: "sine", gain: 2.8, sus: 0.8 },
  ],
};

/** Volume-tecto de referência de um som. Os ganhos por nota multiplicam isto e
 *  vários passam de 1.0 de propósito — quem trata o excesso é o soft-clip. */
const MASTER_GAIN = 0.16;

/** Quanto sinal a tanh do master aceita antes de saturar a sério. 4 dá espaço a
 *  ~4 sons sobrepostos sem cortar: tanh(4) = 0.9993. */
const EXCESSO = 4;

/** Constantes do envelope com patamar (só para notas com `sus`), em segundos.
 *  ATAQUE curto = pancada; QUEDA curta = o "pluc" inicial que dá ataque ao som;
 *  CORTE = o desligar suave no fim, que evita o click. */
const ATAQUE = 0.006;
const QUEDA = 0.05;
const CORTE = 0.07;
/** Abaixo disto não cabe ataque + queda + corte, e a nota volta a ser um pluc.
 *  O teste garante que nenhuma partitura cai aqui por acidente. */
const DURACAO_MINIMA_COM_PATAMAR = ATAQUE + QUEDA + CORTE;

/**
 * Sons que vêm de um FICHEIRO, por nome. O `arrive` é o único.
 *
 * São 1,6 s de AAC tirados do vídeo original SEM re-encode: a faixa de áudio foi
 * remuxada para M4A e os bytes das amostras são os mesmos: o que toca é o som
 * original, não uma cópia recomprimida.
 *
 * O caminho é absoluto e vem de `public/`, como o resto dos media da app
 * (`/campus/...`). Deliberadamente NÃO é um `import`: no bundle, o browser
 * passava a esperar por 26 KB para arrancar, e um som de chegada não vale o
 * primeiro ecrã de ninguém.
 */
const AMOSTRAS: Partial<Record<SoundName, string>> = {
  arrive: "/sounds/arrive.m4a",
};

/** O ganho da amostra, MEDIDO e não arbitrado.
 *
 *  O ficheiro não vem normalizado: descodificado, tem pico 0.530 e −19 dBFS de
 *  RMS. Mandá-lo para a saída como está deixava a chegada ~9 dB ABAIXO da
 *  partitura que veio substituir — o som certo, alto de menos para dar por ele.
 *
 *  Renderizando a cadeia real num OfflineAudioContext, com este ganho e passando
 *  pelo master: pico 0.898, RMS −13.64 dBFS. O `arrive` sintetizado dá pico
 *  0.921 e −13.46 dBFS. São 0.2 dB de diferença: trocar o som NÃO mexe no
 *  equilíbrio da app, e o `poke` (−12.22) continua a ser o mais alto de todos.
 *
 *  E passa pelo MASTER, não directa à saída, ao contrário do que a primeira
 *  versão fazia. A tanh custa aqui uma compressão de pico, mas duas chegadas com
 *  meio segundo de intervalo — 1,6 s de som cada, sobrepõem-se — dão 1.483 de
 *  sinal, que fora do master seria corte a direito. Com ele: pico 0.951, sem
 *  cortar. O soft-clip é o cinto de segurança, e o preço dele é baixo. */
const GANHO_AMOSTRA = 2.8;

/** Duas pessoas a entrar ao mesmo tempo são dois disparos de 1,6 s cada. Sobrepô-
 *  los soma picos (corta) e, a quatro, dá uma papa que já não se lê como "entrou
 *  alguém". Dentro desta janela a segunda chegada é engolida: o som está a tocar
 *  e já disse o que tinha a dizer. */
const INTERVALO_MINIMO = 0.4;

const amostras = new Map<SoundName, AudioBuffer>();
const aCarregar = new Set<SoundName>();
/** Uma falha (offline, 404, browser sem descodificador AAC) não se repete a cada
 *  chegada — sem isto, cada entrada na call disparava outro fetch condenado. */
const semAmostra = new Set<SoundName>();
const ultimoDisparo = new Map<SoundName, number>();

/** Busca e descodifica uma amostra. Falhar aqui não é um erro: é a partitura a
 *  assumir o lugar, que é exactamente para isso que ela continua no ficheiro. */
async function carregarAmostra(name: SoundName, audio: AudioContext): Promise<void> {
  const url = AMOSTRAS[name];
  if (url === undefined) return;
  if (amostras.has(name) || aCarregar.has(name) || semAmostra.has(name)) return;
  if (typeof fetch !== "function") {
    semAmostra.add(name);
    return;
  }
  aCarregar.add(name);
  try {
    const resposta = await fetch(url);
    if (!resposta.ok) throw new Error(String(resposta.status));
    // O `decodeAudioData` devolve PCM já na taxa do contexto — reamostrar de 44,1
    // kHz para a placa de som de quem ouve é trabalho do browser, não nosso.
    amostras.set(name, await audio.decodeAudioData(await resposta.arrayBuffer()));
  } catch {
    semAmostra.add(name);
  } finally {
    aCarregar.delete(name);
  }
}

/** Toca a amostra deste som, se houver uma pronta. Devolve `false` quando quem
 *  tem de tocar é a partitura: ficheiro ainda a caminho, indisponível, ou som
 *  que nunca teve amostra nenhuma. */
function tocarAmostra(name: SoundName, audio: AudioContext, entrada: AudioNode): boolean {
  if (AMOSTRAS[name] === undefined) return false;
  const buffer = amostras.get(name);
  if (!buffer) {
    // Primeira chegada antes de o `primeSounds` ter acabado: pede o ficheiro para
    // a próxima e deixa a partitura cobrir esta.
    void carregarAmostra(name, audio);
    return false;
  }

  const agora = audio.currentTime;
  const ultima = ultimoDisparo.get(name);
  // Engolido — mas com `true`. Cair na síntese aqui trocava o som da marca por um
  // bip a meio dele, que é pior do que o silêncio que se decidiu.
  if (ultima !== undefined && agora - ultima < INTERVALO_MINIMO) return true;

  try {
    const fonte = audio.createBufferSource();
    fonte.buffer = buffer;
    const ganho = audio.createGain();
    ganho.gain.value = GANHO_AMOSTRA;
    fonte.connect(ganho).connect(entrada);
    fonte.start(agora);
    ultimoDisparo.set(name, agora);
    return true;
  } catch {
    return false;
  }
}

type AudioCtor = typeof AudioContext;

/** O contexto e a entrada do master andam sempre juntos — guardados no mesmo
 *  objecto para não poderem ficar dessincronizados. */
let saida: { audio: AudioContext; entrada: AudioNode } | null = null;
/** Uma vez `false`, nunca mais se tenta — evita spam de warnings num browser
 *  sem Web Audio. */
let supported = true;

/**
 * O master: um WaveShaper com uma tanh, que é o que deixa empurrar ganho para
 * dentro sem o resultado estalar.
 *
 * A subtileza está no pré-ganho. A `curve` de um WaveShaperNode é SEMPRE indexada
 * pela entrada no intervalo [-1, 1] e satura (corta a direito) fora dele — pôr lá
 * a tanh directamente não resolveria nada, porque o corte aconteceria na indexação.
 * Por isso divide-se por `EXCESSO` à entrada e guarda-se `tanh(u × EXCESSO)` na
 * curva: um sinal `s` entra como `s/EXCESSO`, sai `tanh(s)`. Ganho unitário para
 * sinal pequeno (tanh(s) ≈ s), compressão progressiva à medida que sobe, e nunca
 * passa de 1.
 */
function construirMaster(audio: AudioContext): AudioNode {
  // ÍMPAR de propósito: assim existe um ponto exactamente em u = 0, que a curva
  // mapeia a tanh(0) = 0. Com um número par, o zero cai entre dois pontos e a
  // interpolação introduz uma componente contínua — inaudível, mas fica lá a
  // empurrar a coluna do altifalante.
  const pontos = 4097;
  const curva = new Float32Array(pontos);
  for (let i = 0; i < pontos; i++) {
    const u = (i / (pontos - 1)) * 2 - 1;
    curva[i] = Math.tanh(u * EXCESSO);
  }
  const forma = audio.createWaveShaper();
  forma.curve = curva;
  // A não-linearidade gera harmónicos novos; sem sobre-amostragem, os que passam
  // de Nyquist dobram para trás e ouvem-se como um zumbido metálico.
  forma.oversample = "4x";

  const entrada = audio.createGain();
  entrada.gain.value = 1 / EXCESSO;
  entrada.connect(forma).connect(audio.destination);
  return entrada;
}

function context(): { audio: AudioContext; entrada: AudioNode } | null {
  if (!supported) return null;
  if (saida) return saida;
  if (typeof window === "undefined") return null;

  const Ctor: AudioCtor | undefined =
    window.AudioContext ?? (window as { webkitAudioContext?: AudioCtor }).webkitAudioContext;
  if (!Ctor) {
    supported = false;
    return null;
  }
  try {
    const audio = new Ctor();
    saida = { audio, entrada: construirMaster(audio) };
    return saida;
  } catch {
    supported = false;
    return null;
  }
}

/**
 * Destranca o áudio a partir de um gesto do utilizador (um clique). O browser
 * cria o AudioContext em estado `suspended` até haver interacção; chamar isto no
 * primeiro clique real garante que o "join" já se ouve. Barato e idempotente.
 *
 * É também aqui que as amostras são pedidas. O sítio certo é este e não o
 * arranque da app: a primeira pessoa pode entrar meio segundo depois do meu
 * clique, e um som que só fica pronto à SEGUNDA chegada é um som partido.
 */
export function primeSounds(): void {
  const alvo = context();
  if (!alvo) return;
  if (alvo.audio.state === "suspended") void alvo.audio.resume().catch(() => undefined);
  for (const nome of Object.keys(AMOSTRAS) as SoundName[]) void carregarAmostra(nome, alvo.audio);
}

/**
 * Toca um som. Não devolve nada e nunca lança: quem chama trata isto como um
 * enfeite, não como uma operação que possa falhar a lógica à volta.
 */
export function playSound(name: SoundName): void {
  const alvo = context();
  if (!alvo) return;
  const { audio, entrada } = alvo;

  // Aba em segundo plano com o contexto suspenso: tentar retomar é barato e faz
  // o "alguém entrou" ouvir-se quando se volta ao separador.
  if (audio.state === "suspended") void audio.resume().catch(() => undefined);

  // O ficheiro tem precedência sobre a partitura do mesmo nome; a partitura só
  // toca quando ele não pode.
  if (tocarAmostra(name, audio, entrada)) return;

  const score = SCORES[name];
  const start = audio.currentTime;

  try {
    for (const note of score) {
      const osc = audio.createOscillator();
      const env = audio.createGain();
      osc.type = note.type ?? "sine";
      osc.frequency.value = note.f;

      const t0 = start + note.t;
      const t1 = t0 + note.d;
      const peak = MASTER_GAIN * (note.gain ?? 1);
      const comPatamar = note.sus !== undefined && note.d > DURACAO_MINIMA_COM_PATAMAR;

      // exponentialRampTo não aceita 0 — daí o alvo minúsculo em vez de silêncio.
      env.gain.setValueAtTime(0.0001, t0);

      if (comPatamar) {
        // Ataque → queda ao patamar → patamar → corte. O patamar é a única parte
        // que produz volume SUSTENTADO; sem ele, `d` é decorativo.
        const nivel = peak * (note.sus ?? 1);
        const fimDaQueda = t0 + ATAQUE + QUEDA;
        const inicioDoCorte = Math.max(fimDaQueda + 0.001, t1 - CORTE);
        env.gain.exponentialRampToValueAtTime(peak, t0 + ATAQUE);
        env.gain.exponentialRampToValueAtTime(nivel, fimDaQueda);
        env.gain.setValueAtTime(nivel, inicioDoCorte);
        env.gain.exponentialRampToValueAtTime(0.0001, t1);
      } else {
        // Pluc: rampa curta para o pico, cauda exponencial até ~silêncio.
        env.gain.exponentialRampToValueAtTime(peak, t0 + 0.008);
        env.gain.exponentialRampToValueAtTime(0.0001, t1);
      }

      osc.connect(env).connect(entrada);
      osc.start(t0);
      osc.stop(t1 + 0.02);
    }
  } catch {
    // Um som que não sai não é uma falha do produto.
  }
}

/**
 * Toca um som em repetição até se chamar a função que devolve — o toque e o
 * "a chamar" das ligações privadas. O primeiro sai já; os seguintes a cada
 * `periodoMs`. Quem chama TEM de chamar a paragem (é o retorno de um efeito).
 */
export function tocarEmRepeticao(name: SoundName, periodoMs: number): () => void {
  playSound(name);
  const intervalo = setInterval(() => playSound(name), periodoMs);
  return () => clearInterval(intervalo);
}
